import { motion } from "framer-motion";
import { Plane, Briefcase, MapPin, GraduationCap, Users, Rocket, ArrowUpRight } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { ZelligeFill } from "@/components/marocanada/ZelligePattern";
import { trackCtaClick, getEligibilityUrl } from "@/lib/analytics";

const ICONS = [Plane, Briefcase, MapPin, GraduationCap, Users, Rocket];

/**
 * Asymmetric services grid — 3 columns, cards at different heights.
 * One card breaks the grid and overlaps the section border.
 * Hover: zellige tile texture floods in at 8%, card lifts.
 */
const Services = () => {
  const { t, lang } = useLang();

  // Per-card layout: row-span + offset to break the grid
  const layouts = [
    { rowSpan: 2, marginTop: 0, breakBottom: false },     // 0
    { rowSpan: 1, marginTop: 48, breakBottom: false },    // 1
    { rowSpan: 1, marginTop: 0, breakBottom: false },     // 2
    { rowSpan: 1, marginTop: -32, breakBottom: false },   // 3
    { rowSpan: 2, marginTop: 24, breakBottom: true },     // 4 — breaks grid
    { rowSpan: 1, marginTop: 0, breakBottom: false },     // 5
  ];

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative bg-[#0D1117] py-28 sm:py-36 overflow-hidden"
    >
      <div className="noise-soft" />

      <div className="relative max-w-[1320px] mx-auto px-6 lg:px-10">
        <div className="mb-16 max-w-3xl">
          <span className="brushstroke mb-6" />
          <div className="font-mono-accent text-[10px] text-[#E8A045] mb-5">
            {t.services.eyebrow}
          </div>
          <h2
            data-testid="services-headline"
            className="font-display text-5xl sm:text-6xl lg:text-[4.5rem] text-[#F5F2EE] leading-[1.02] tracking-tight"
          >
            {t.services.headline}
          </h2>
        </div>

        {/* Asymmetric grid */}
        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
          style={{ gridAutoRows: "auto" }}
        >
          {t.services.list.map((s, i) => {
            const Icon = ICONS[i] || Plane;
            const layout = layouts[i] || layouts[0];
            return (
              <motion.article
                key={i}
                data-testid={`service-card-${i}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{
                  duration: 0.8,
                  delay: (i % 3) * 0.1,
                  ease: [0.16, 1, 0.3, 1],
                }}
                className={`card-inner-border group relative bg-[#1C1C1E] p-8 lg:p-10 flex flex-col min-h-[300px] ${
                  layout.breakBottom ? "lg:translate-y-12" : ""
                }`}
                style={{
                  marginTop:
                    typeof window !== "undefined" && window.innerWidth >= 1024
                      ? `${layout.marginTop}px`
                      : 0,
                  ...(layout.rowSpan > 1 ? { gridRow: `span ${layout.rowSpan}` } : {}),
                }}
              >
                {/* Zellige hover texture */}
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-[0.08] transition-opacity duration-500 pointer-events-none"
                >
                  <ZelligeFill
                    className="w-full h-full"
                    id={`zellige-card-${i}`}
                    stroke="#E8A045"
                    strokeAlt="#6B4F3A"
                    size={64}
                  />
                </div>

                {/* Step index */}
                <div className="absolute top-6 right-6 font-mono-accent text-[10px] text-[#E8A045]/70">
                  0{i + 1}
                </div>

                <Icon
                  className="w-7 h-7 text-[#E8A045] mb-8 relative z-10"
                  strokeWidth={1.3}
                />

                <h3 className="font-display text-2xl lg:text-[1.75rem] text-[#F5F2EE] tracking-tight leading-tight mb-4 relative z-10 min-h-[3rem]">
                  {s.name}
                </h3>
                <p className="font-body text-[15px] text-[#F5F2EE]/55 leading-relaxed relative z-10 flex-1">
                  {s.desc}
                </p>

                <div className="mt-8 h-px bg-gradient-to-r from-[#E8A045]/20 to-transparent relative z-10" />

                <a
                  href={getEligibilityUrl(lang)}
                  data-testid={`service-card-cta-${i}`}
                  onClick={() => trackCtaClick(`service-${i}`)}
                  className="mt-5 inline-flex items-center gap-2 font-mono-accent text-[10px] text-[#F5F2EE]/75 hover:text-[#E8A045] transition-colors self-start relative z-10"
                >
                  <span>{t.services.cta}</span>
                  <ArrowUpRight
                    className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1 group-hover:-translate-y-0.5"
                    strokeWidth={1.5}
                  />
                </a>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
